import React, { useState, useEffect, useRef } from 'react';

const isMobile = window.matchMedia('(pointer: coarse)').matches;

// ── Constants ─────────────────────────────────────────────────────────────────
const ACCENT = '#F59E0B';
const BG = '#0d0d1a';
const AXES = ['empathy', 'accountability', 'autonomy', 'harmony', 'pragmatism'];
const AXIS_LABELS = ['Empathy', 'Accountability', 'Autonomy', 'Harmony', 'Pragmatism'];
const MIN_VOTES_FOR_PROFILE = 5;

const OPTIONS = [
  { label: 'Yes', value: 'yes', description: "They're in the wrong" },
  { label: 'No', value: 'no', description: "They're not in the wrong" },
];

const WEIGHTS = {
  yes: { empathy: 0.5, accountability: 0.5, autonomy: -0.4, harmony: 0.2, pragmatism: -0.1 },
  no:  { empathy: -0.1, accountability: -0.2, autonomy: 0.6, harmony: -0.1, pragmatism: 0.4 },
};

const ARCHETYPES = [
  { id: 'empath',      name: 'The Empath',      emoji: '💝', primaryAxis: 'empathy',        description: 'You lead with your heart. When you see someone hurting, you feel it too — and that shapes every call you make.' },
  { id: 'judge',       name: 'The Judge',        emoji: '⚖️', primaryAxis: 'accountability', description: "You call it like you see it. Actions have consequences, and you don't let sentimentality cloud the ledger." },
  { id: 'maverick',    name: 'The Maverick',     emoji: '🔥', primaryAxis: 'autonomy',       description: "People have the right to live on their own terms. You back personal freedom — even when it makes things messy." },
  { id: 'peacekeeper', name: 'The Peacekeeper',  emoji: '🕊️', primaryAxis: 'harmony',        description: "You hate unnecessary conflict. Life's too short — you'd rather find common ground than draw battle lines." },
  { id: 'pragmatist',  name: 'The Pragmatist',   emoji: '🧠', primaryAxis: 'pragmatism',     description: "You cut through the noise. What matters is what works — not whose feelings got hurt in the process." },
];

// ── Utilities ─────────────────────────────────────────────────────────────────
async function getVoterHash() {
  const stored = localStorage.getItem('itw_voter_hash');
  if (stored) return stored;

  let ip = 'fallback-' + Math.random().toString(36).slice(2);
  try {
    const res = await fetch('/api/getIp');
    if (res.ok) {
      const data = await res.json();
      ip = data.ip || ip;
    }
  } catch { /* use fallback */ }

  const encoded = new TextEncoder().encode(ip + '_itw_v1');
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  const hash = Array.from(new Uint8Array(hashBuffer))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
  localStorage.setItem('itw_voter_hash', hash);
  return hash;
}

function getVotedMap() {
  return JSON.parse(localStorage.getItem('itw_voted') || '{}');
}

function saveVotedMap(map) {
  localStorage.setItem('itw_voted', JSON.stringify(map));
}

function computePersonalityScores(entries) {
  if (!entries.length) return null;
  const sums = Object.fromEntries(AXES.map(a => [a, 0]));
  let count = 0;
  for (const { weights } of entries) {
    if (!weights) continue;
    AXES.forEach(a => { sums[a] += weights[a] || 0; });
    count++;
  }
  if (!count) return null;
  return Object.fromEntries(AXES.map(a => [a, sums[a] / count]));
}

function getArchetype(scores) {
  if (!scores) return null;
  const best = AXES.reduce((a, b) => scores[a] >= scores[b] ? a : b);
  return ARCHETYPES.find(arc => arc.primaryAxis === best) || ARCHETYPES[0];
}

function dateLabel() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

// ── Sub-components ────────────────────────────────────────────────────────────
function OptionButton({ opt, selected, onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  const active = selected || (hovered && !disabled);
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        padding: '14px 20px',
        borderRadius: 14,
        border: `1.5px solid ${active ? ACCENT : 'rgba(255,255,255,0.15)'}`,
        background: selected ? 'rgba(245,158,11,0.15)' : hovered && !disabled ? 'rgba(245,158,11,0.07)' : 'transparent',
        color: active ? ACCENT : 'rgba(255,255,255,0.88)',
        cursor: disabled ? 'default' : 'pointer',
        marginBottom: 10,
        textAlign: 'left',
        fontFamily: 'Nunito, sans-serif',
        fontWeight: 700,
        fontSize: 16,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        transition: 'all 0.15s',
        opacity: disabled && !selected ? 0.45 : 1,
      }}
    >
      <span style={{ fontSize: 15, minWidth: 44, fontFamily: "'Fredoka One', cursive", letterSpacing: 0.5 }}>
        {opt.label}
      </span>
      {opt.description && (
        <span style={{ fontSize: 13, opacity: 0.65, fontWeight: 400 }}>{opt.description}</span>
      )}
    </button>
  );
}

function ResultsBar({ counts, total, personalVote }) {
  const topValue = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
  return (
    <div style={{ width: '100%' }}>
      {OPTIONS.map(opt => {
        const count = counts[opt.value] || 0;
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        const isMe = opt.value === personalVote;
        const isTop = opt.value === topValue;
        const barColor = isMe ? ACCENT : isTop ? '#8B5CF6' : 'rgba(255,255,255,0.22)';
        return (
          <div key={opt.value} style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5, alignItems: 'center' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: isMe ? ACCENT : 'rgba(255,255,255,0.88)', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'Nunito, sans-serif' }}>
                {opt.label}
                {isMe && <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.75 }}>← you</span>}
              </span>
              <span style={{ fontSize: 14, fontWeight: 700, color: 'rgba(255,255,255,0.65)', fontFamily: 'Nunito, sans-serif' }}>{pct}%</span>
            </div>
            <div style={{ height: 9, borderRadius: 5, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 5, transition: 'width 0.9s cubic-bezier(0.4, 0, 0.2, 1)' }} />
            </div>
          </div>
        );
      })}
      {total > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: 12, color: 'rgba(255,255,255,0.25)', textAlign: 'right', fontFamily: 'Nunito, sans-serif' }}>
          based on {total.toLocaleString()} Reddit comment{total !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

function RadarChart({ scores, size: rawSize = 220 }) {
  const size = isMobile ? Math.min(rawSize, window.innerWidth - 64) : rawSize;
  const cx = size / 2, cy = size / 2;
  const maxR = size * 0.32, labelR = size * 0.46;
  const angles = AXES.map((_, i) => (i * 2 * Math.PI / AXES.length) - Math.PI / 2);
  const normalized = AXES.map(a => Math.max(0.06, Math.min(1, ((scores?.[a] ?? 0) + 1) / 2)));
  const dataPoints = angles.map((angle, i) => [cx + normalized[i] * maxR * Math.cos(angle), cy + normalized[i] * maxR * Math.sin(angle)]);
  const gridPolygons = [0.25, 0.5, 0.75, 1.0].map(level => angles.map(a => [cx + level * maxR * Math.cos(a), cy + level * maxR * Math.sin(a)]));
  const toPolyPoints = pts => pts.map(p => p.join(',')).join(' ');
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible', maxWidth: '100%' }}>
      {gridPolygons.map((pts, gi) => <polygon key={gi} points={toPolyPoints(pts)} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={1} />)}
      {angles.map((a, i) => <line key={i} x1={cx} y1={cy} x2={cx + maxR * Math.cos(a)} y2={cy + maxR * Math.sin(a)} stroke="rgba(255,255,255,0.12)" strokeWidth={1} />)}
      <polygon points={toPolyPoints(dataPoints)} fill={ACCENT + '2e'} stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" />
      {dataPoints.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={4} fill={ACCENT} />)}
      {AXES.map((_, i) => {
        const lx = cx + labelR * Math.cos(angles[i]), ly = cy + labelR * Math.sin(angles[i]);
        return <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fill="rgba(255,255,255,0.8)" fontSize={11} fontFamily="Nunito, sans-serif" fontWeight="700">{AXIS_LABELS[i]}</text>;
      })}
    </svg>
  );
}

function ProfileView({ voteHistory, onBack }) {
  const scores = computePersonalityScores(voteHistory);
  const archetype = getArchetype(scores);
  return (
    <div style={pageStyle}>
      <div style={{ width: '100%', maxWidth: 600, marginBottom: 24 }}>
        <button onClick={onBack} style={ghostBtnStyle}>← Back</button>
      </div>
      <div style={{ ...cardStyle, textAlign: 'center', opacity: 1 }}>
        <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 26, color: ACCENT, margin: '0 0 6px' }}>Your Profile</h2>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, margin: '0 0 28px', fontFamily: 'Nunito, sans-serif' }}>
          Based on {voteHistory.length} question{voteHistory.length !== 1 ? 's' : ''}
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <RadarChart scores={scores} />
        </div>
        {archetype && (
          <div style={{ background: 'rgba(245,158,11,0.07)', borderRadius: 16, padding: '20px 24px', border: '1px solid rgba(245,158,11,0.18)' }}>
            <div style={{ fontSize: 38, marginBottom: 8 }}>{archetype.emoji}</div>
            <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: 22, color: ACCENT, marginBottom: 10 }}>{archetype.name}</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, fontFamily: 'Nunito, sans-serif' }}>{archetype.description}</div>
          </div>
        )}
        <p style={{ marginTop: 20, fontSize: 12, color: 'rgba(255,255,255,0.2)', fontFamily: 'Nunito, sans-serif' }}>
          Scores shift as you answer more questions.
        </p>
      </div>
    </div>
  );
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const pageStyle = { minHeight: '100vh', background: BG, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '28px 16px 72px', fontFamily: 'Nunito, sans-serif' };
const cardStyle = { width: '100%', maxWidth: 600, background: 'rgba(255,255,255,0.04)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.08)', padding: isMobile ? '24px 16px' : '32px 28px' };
const ghostBtnStyle = { background: 'transparent', border: '1.5px solid rgba(255,255,255,0.15)', borderRadius: 50, padding: '8px 18px', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: 13 };

// ── Main Component ────────────────────────────────────────────────────────────
export default function InTheWrong() {
  const [post, setPost] = useState(null);
  const [phase, setPhase] = useState('loading'); // loading | personal | crowd | results | error
  const [personalVote, setPersonalVote] = useState(null);
  const [crowdVote, setCrowdVote] = useState(null);
  const [results, setResults] = useState(null);
  const [crowdCorrect, setCrowdCorrect] = useState(null);
  const [voteHistory, setVoteHistory] = useState([]);
  const [fadingIn, setFadingIn] = useState(true);
  const [showProfile, setShowProfile] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    async function init() {
      await getVoterHash();

      const voted = getVotedMap();
      const history = Object.values(voted)
        .map(e => e.personalWeights ? { weights: e.personalWeights } : null)
        .filter(Boolean);
      setVoteHistory(history);

      let qotd = null;
      try {
        const res = await fetch('/api/redditQotd');
        if (res.ok) {
          const data = await res.json();
          qotd = data.post || null;
        }
      } catch { /* handled below */ }

      if (!qotd) {
        setPhase('error');
        return;
      }

      setPost(qotd);

      // Already voted — jump straight to results
      if (voted[qotd.id]) {
        setPersonalVote(voted[qotd.id].personalVote);
        try {
          const res = await fetch(`/api/redditComments?id=${qotd.id}`);
          if (res.ok) setResults(await res.json());
        } catch { /* show no results */ }
        setPhase('results');
        return;
      }

      setPhase('personal');
    }
    init();
  }, []);

  function fadeTransition(cb) {
    setFadingIn(false);
    setTimeout(() => { cb(); setFadingIn(true); }, 200);
  }

  function handlePersonalVote(value) {
    setPersonalVote(value);
    setTimeout(() => fadeTransition(() => setPhase('crowd')), 120);
  }

  async function handleCrowdVote(value) {
    if (submittingRef.current || !post) return;
    submittingRef.current = true;
    setCrowdVote(value);

    let resultData = null;
    try {
      const res = await fetch(`/api/redditComments?id=${post.id}`);
      if (res.ok) resultData = await res.json();
    } catch { /* show no results */ }

    setResults(resultData);

    if (resultData && resultData.total > 0) {
      const topVote = Object.entries(resultData.counts).sort((a, b) => b[1] - a[1])[0]?.[0];
      setCrowdCorrect(value === topVote);
    }

    const weights = WEIGHTS[personalVote];
    const voted = getVotedMap();
    voted[post.id] = { questionId: post.id, personalVote, personalWeights: weights };
    saveVotedMap(voted);
    setVoteHistory(prev => [...prev, { weights }]);

    submittingRef.current = false;
    fadeTransition(() => setPhase('results'));
  }

  const personalityScores = computePersonalityScores(voteHistory);
  const archetype = getArchetype(personalityScores);
  const profileUnlocked = voteHistory.length >= MIN_VOTES_FOR_PROFILE;
  const votesLeft = Math.max(0, MIN_VOTES_FOR_PROFILE - voteHistory.length);

  if (showProfile && profileUnlocked) {
    return <ProfileView voteHistory={voteHistory} onBack={() => setShowProfile(false)} />;
  }

  if (phase === 'loading') {
    return <div style={pageStyle}><p style={{ color: 'rgba(255,255,255,0.3)', marginTop: 80 }}>Loading today's question...</p></div>;
  }

  if (phase === 'error' || !post) {
    return (
      <div style={pageStyle}>
        <div style={{ ...cardStyle, opacity: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 14 }}>😬</div>
          <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 24, color: ACCENT, margin: '0 0 10px' }}>Nothing today</h2>
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: 14 }}>Couldn't load a question from Reddit. Try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{ width: '100%', maxWidth: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 30, color: ACCENT, margin: 0 }}>In The Wrong?</h1>
        {profileUnlocked ? (
          <button onClick={() => setShowProfile(true)} style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 50, padding: '8px 16px', color: ACCENT, cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: 13 }}>
            {archetype?.emoji} My profile
          </button>
        ) : (
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', fontFamily: 'Nunito, sans-serif' }}>
            {votesLeft} more to unlock profile
          </span>
        )}
      </div>

      {/* Card */}
      <div style={{ ...cardStyle, opacity: fadingIn ? 1 : 0, transition: 'opacity 0.2s ease' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 11, fontFamily: 'Nunito, sans-serif', color: 'rgba(255,255,255,0.28)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Today's question · {dateLabel()}
          </span>
          {phase === 'results' && post.numComments > 0 && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.2)', fontFamily: 'Nunito, sans-serif' }}>
              {post.numComments.toLocaleString()} comments
            </span>
          )}
        </div>

        <h2 style={{ fontFamily: "'Fredoka One', cursive", fontSize: 21, color: 'white', margin: '0 0 14px', lineHeight: 1.35 }}>
          {post.title}
        </h2>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, margin: '0 0 20px', whiteSpace: 'pre-line' }}>
          {post.body}
        </p>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 20 }} />

        {phase === 'personal' && (
          <div>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', margin: '0 0 4px', fontWeight: 700 }}>
              Are they in the wrong?
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', margin: '0 0 16px', fontStyle: 'italic' }}>
              If you can't decide, pick the one you're leaning more towards.
            </p>
            {OPTIONS.map(opt => (
              <OptionButton key={opt.value} opt={opt} selected={opt.value === personalVote} disabled={personalVote !== null} onClick={() => !personalVote && handlePersonalVote(opt.value)} />
            ))}
          </div>
        )}

        {phase === 'crowd' && (
          <div>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.38)', margin: '0 0 4px', fontStyle: 'italic' }}>
              You said: <strong style={{ color: ACCENT }}>{OPTIONS.find(o => o.value === personalVote)?.label}</strong>
            </p>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', margin: '0 0 4px', fontWeight: 700 }}>
              What does Reddit think?
            </p>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', margin: '0 0 16px', fontStyle: 'italic' }}>
              Take your best guess.
            </p>
            {OPTIONS.map(opt => (
              <OptionButton key={opt.value} opt={opt} selected={opt.value === crowdVote} disabled={crowdVote !== null} onClick={() => handleCrowdVote(opt.value)} />
            ))}
            {crowdVote && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.25)', margin: '8px 0 0', textAlign: 'center', fontStyle: 'italic' }}>Counting comments...</p>}
          </div>
        )}

        {phase === 'results' && (
          <div>
            {crowdCorrect !== null && (
              <div style={{ background: crowdCorrect ? 'rgba(16,185,129,0.09)' : 'rgba(139,92,246,0.09)', border: `1px solid ${crowdCorrect ? 'rgba(16,185,129,0.28)' : 'rgba(139,92,246,0.28)'}`, borderRadius: 12, padding: '10px 16px', marginBottom: 20, fontSize: 14, color: crowdCorrect ? '#6EE7B7' : '#C4B5FD', fontFamily: 'Nunito, sans-serif', fontWeight: 700 }}>
                {crowdCorrect ? '🎯 You read the room!' : '🤔 The crowd surprised you'}
              </div>
            )}

            {results && results.total > 0 ? (
              <ResultsBar counts={results.counts} total={results.total} personalVote={personalVote} />
            ) : (
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', marginBottom: 16 }}>
                Comment data unavailable for this post.
              </p>
            )}

            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', marginTop: 20, textAlign: 'center', fontFamily: 'Nunito, sans-serif' }}>
              Come back tomorrow for a new question.
            </p>

            {!profileUnlocked ? (
              <div style={{ marginTop: 20, padding: '12px 16px', background: 'rgba(139,92,246,0.07)', borderRadius: 12, border: '1px solid rgba(139,92,246,0.14)', fontSize: 13, color: 'rgba(255,255,255,0.42)', textAlign: 'center', fontStyle: 'italic', fontFamily: 'Nunito, sans-serif' }}>
                Answer {votesLeft} more to unlock your profile ✨
              </div>
            ) : (
              <div onClick={() => setShowProfile(true)} style={{ marginTop: 20, padding: '12px 16px', background: 'rgba(245,158,11,0.07)', borderRadius: 12, border: '1px solid rgba(245,158,11,0.15)', fontSize: 13, color: ACCENT, textAlign: 'center', cursor: 'pointer', fontWeight: 700, fontFamily: 'Nunito, sans-serif' }}>
                {archetype?.emoji} You're {archetype?.name} — see your profile →
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
